// src/page/PurchasedTicketModal.jsx

import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import PropTypes from 'prop-types';
import { FaTicketAlt, FaTimesCircle } from "react-icons/fa";

const BRAND_BLUE = "#0056A6";
const BRAND_ORANGE = "#FF9000";

/**
 * PurchasedTicketModal Component
 *
 * Displays detailed information about a purchased ticket along with a QR code.
 *
 * @param {Object} ticket - The user ticket object containing all necessary details.
 * @param {boolean} isOpen - Flag to determine if the modal should be displayed.
 * @param {Function} onClose - Function to handle closing the modal.
 */
const PurchasedTicketModal = ({ ticket, isOpen, onClose }) => {
  if (!isOpen || !ticket) return null;

  console.log("Ticket data received:", ticket);
  
  // Validate ticket completion status - should only show completed tickets
  const isCompleted = ticket.payment_status === 'completed';
  const hasQrCode = Boolean(ticket.qr_code || ticket.qr_code_url);
  
  // Extra validation that should not normally be needed since filtered by PurchaseHistory
  if (!isCompleted || !hasQrCode) {
    console.warn(`Attempted to show incomplete ticket: 
      Payment Status: ${ticket.payment_status}, 
      Has QR: ${hasQrCode}, 
      Reference: ${ticket.reference_id}`
    );
  }

  // Create data for QR code (fallback if no server QR is available)
  const qrData = JSON.stringify({
    reference_id: ticket.reference_id,
    event_id: ticket.event,
    ticket_id: ticket.ticket,
    user_id: ticket.user,
    event_name: ticket.event_name || "Event",
    ticket_quantity: ticket.ticket_quantity || 1,
    payment_status: ticket.payment_status,
  });

  // Get ticket category and format it properly
  const getTicketCategory = () => {
    if (!ticket.ticket_category) return "N/A";
    return ticket.ticket_category.charAt(0).toUpperCase() + ticket.ticket_category.slice(1);
  };

  // Payment status badge
  const statusBadge = {
    completed: <span style={{background: "#E6F4FF", color: BRAND_BLUE}} className="px-3 py-1 rounded-full text-xs font-semibold">Completed</span>,
    pending: <span style={{background: "#FFF4E0", color: BRAND_ORANGE}} className="px-3 py-1 rounded-full text-xs font-semibold">Processing</span>,
    failed: <span style={{background: "#FFE6E6", color: "#D32F2F"}} className="px-3 py-1 rounded-full text-xs font-semibold">Failed</span>,
  }[ticket.payment_status] || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full relative overflow-y-auto max-h-[90vh] border border-gray-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between rounded-t-2xl px-6 py-4"
          style={{
            background: `linear-gradient(90deg, ${BRAND_BLUE} 60%, ${BRAND_ORANGE} 100%)`
          }}
        >
          <div className="flex items-center gap-3">
            <FaTicketAlt className="text-white text-2xl" />
            <h2 className="text-white text-xl font-bold">
              Ticket for {ticket.event_name || "Event"}
            </h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl">
            <FaTimesCircle />
          </button>
        </div>

        {/* Ticket Details */}
        <div className="px-8 py-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Details Column */}
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-2">
                {statusBadge}
                <span className="text-xs text-gray-400">Ref: {ticket.reference_id || "N/A"}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="font-semibold text-gray-700">Event:</div>
                <div>{ticket.event_name || "N/A"}</div>
                <div className="font-semibold text-gray-700">Venue:</div>
                <div>{ticket.venue_name || "N/A"}</div>
                <div className="font-semibold text-gray-700">Address:</div>
                <div>{ticket.venue_address || "N/A"}</div>
                <div className="font-semibold text-gray-700">Start Time:</div>
                <div>{ticket.start_time || "N/A"}</div>
                <div className="font-semibold text-gray-700">End Time:</div>
                <div>{ticket.end_time || "N/A"}</div>
                <div className="font-semibold text-gray-700">Category:</div>
                <div>{getTicketCategory()}</div>
                <div className="font-semibold text-gray-700">Quantity:</div>
                <div>{ticket.ticket_quantity || 1}</div>
                <div className="font-semibold text-gray-700">Price:</div>
                <div>{ticket.price ? `£${ticket.price}` : "Free"}</div>
                <div className="font-semibold text-gray-700">Total:</div>
                <div>{ticket.total ? `£${ticket.total}` : "Free"}</div>
                <div className="font-semibold text-gray-700">Purchase Date:</div>
                <div>{ticket.purchase_date_display || ticket.created_at?.substring(0, 10) || "N/A"}</div>
                <div className="font-semibold text-gray-700">Payment Method:</div>
                <div>{ticket.payment_method || "N/A"}</div>
              </div>
            </div>
            {/* QR Code Column */}
            <div className="flex flex-col items-center justify-center">
              <div
                className="bg-white border-2 rounded-xl shadow-lg p-3 mb-2"
                style={{ borderColor: BRAND_ORANGE }}
              >
                {isCompleted && hasQrCode ? (
                  <img
                    src={ticket.qr_code_url || ticket.qr_code}
                    alt="QR Code"
                    className="w-40 h-40 object-contain"
                    onError={e => {
                      e.target.style.display = 'none';
                      document.getElementById('fallback-qr').style.display = 'block';
                    }}
                  />
                ) : (
                  <QRCodeCanvas
                    value={qrData}
                    size={160}
                    level="H"
                    includeMargin={true}
                  />
                )}
                {/* Fallback QR code if image fails to load */}
                <div id="fallback-qr" style={{ display: 'none' }}>
                  <QRCodeCanvas value={qrData} size={160} level="H" />
                </div>
              </div>
              <div className="text-xs text-gray-500 text-center mt-1">
                {isCompleted && hasQrCode
                  ? "Scan this QR code at the event entrance."
                  : "QR code will appear when payment is completed."}
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="flex justify-end px-8 pb-6">
          <button
            onClick={onClose}
            style={{
              background: BRAND_ORANGE,
              color: "#fff",
              fontWeight: 600
            }}
            className="hover:bg-orange-600 text-white py-2 px-6 rounded-full shadow transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Prop type validation
PurchasedTicketModal.propTypes = {
  ticket: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PurchasedTicketModal;
